import { useCallback, useState } from 'react';
import { AlertType, ButtonColor, ButtonGroup, ConfirmModal, Footer, TextButton } from '../../components';
import { useHandleApiResponse, useToast } from '../../hooks';
import {
  useArchiveCoachingTemplateMutation,
  useCreateCoachingTemplateMutation,
  useDeleteCoachingTemplateMutation,
  useUpdateCoachingTemplateMutation,
} from '../../services';
import { CoachingTemplate } from '../../types';

const CREATE_ERROR_MSG = 'Failed to create coaching template';
const UPDATE_ERROR_MSG = 'Failed to update coaching template';
const DELETE_ERROR_MSG = 'Failed to delete coaching template';
const ARCHIVE_ERROR_MSG = 'Failed to archive coaching template';

interface ManageCoachingTemplateFooterProps {
  navigateToCoachingRubrics: () => void;
  existingCoachingTemplate?: CoachingTemplate;
  coachingTemplateName?: string;
  coachingTemplatePrompt?: string;
}

const ManageCoachingTemplateFooter = ({
  existingCoachingTemplate,
  coachingTemplateName,
  coachingTemplatePrompt,
  navigateToCoachingRubrics,
}: ManageCoachingTemplateFooterProps) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const {
    id: existingTemplateId,
    isUsed: isExistingTemplateUsed,
    isDefault: isExistingTemplateDefault,
  } = existingCoachingTemplate || {};

  const [updateCoachingTemplate, { isLoading: isUpdatingCoaching }] = useUpdateCoachingTemplateMutation();
  const [createCoachingTemplate, { isLoading: isCreatingCoaching }] = useCreateCoachingTemplateMutation();
  const [deleteCoachingTemplate, { isLoading: isDeletingCoaching }] = useDeleteCoachingTemplateMutation();
  const [archiveCoachingTemplate, { isLoading: isArchivingCoaching }] = useArchiveCoachingTemplateMutation();

  const handleApiResponse = useHandleApiResponse();
  const { showToast } = useToast();

  // Handles the creation of a new coaching template
  const handleCreateCoachingTemplate = useCallback(async () => {
    if (!coachingTemplateName || !coachingTemplatePrompt) return;

    try {
      const response = await createCoachingTemplate({
        name: coachingTemplateName,
        prompt: coachingTemplatePrompt,
      });
      handleApiResponse({
        response,
        errorMsg: CREATE_ERROR_MSG,
        onSuccess: navigateToCoachingRubrics,
      });
    } catch (error) {
      console.error(`${CREATE_ERROR_MSG}: ${error}`);
      showToast({
        message: CREATE_ERROR_MSG,
        type: AlertType.ERROR,
      });
    }
  }, [
    coachingTemplateName,
    coachingTemplatePrompt,
    createCoachingTemplate,
    handleApiResponse,
    navigateToCoachingRubrics,
    showToast,
  ]);

  // Delete coaching template handler
  const handleDeleteCoachingTemplate = useCallback(async () => {
    // If coaching template is not found/created, just navigate to the templates page
    if (!existingTemplateId) return navigateToCoachingRubrics();

    try {
      const response = await deleteCoachingTemplate(existingTemplateId);
      handleApiResponse({
        response,
        errorMsg: DELETE_ERROR_MSG,
        onSuccess: navigateToCoachingRubrics,
      });
    } catch (error) {
      console.error(`${DELETE_ERROR_MSG}: ${error}`);
      showToast({
        message: DELETE_ERROR_MSG,
        type: AlertType.ERROR,
      });
    }
  }, [existingTemplateId, deleteCoachingTemplate, handleApiResponse, navigateToCoachingRubrics, showToast]);

  // Archive coaching template handler
  const handleArchiveCoachingTemplate = useCallback(async () => {
    if (!existingTemplateId) return navigateToCoachingRubrics();

    try {
      const response = await archiveCoachingTemplate(existingTemplateId);
      handleApiResponse({
        response,
        errorMsg: ARCHIVE_ERROR_MSG,
        onSuccess: navigateToCoachingRubrics,
      });
    } catch (error) {
      console.error(`${ARCHIVE_ERROR_MSG}: ${error}`);
      showToast({
        message: ARCHIVE_ERROR_MSG,
        type: AlertType.ERROR,
      });
    }
  }, [existingTemplateId, archiveCoachingTemplate, handleApiResponse, navigateToCoachingRubrics, showToast]);

  const handleEditCoachingTemplate = useCallback(async () => {
    if (!existingTemplateId) return navigateToCoachingRubrics();

    try {
      const response = await updateCoachingTemplate({
        id: existingTemplateId,
        name: coachingTemplateName,
        prompt: coachingTemplatePrompt,
      });
      handleApiResponse({
        response,
        errorMsg: UPDATE_ERROR_MSG,
        onSuccess: navigateToCoachingRubrics,
      });
    } catch (error) {
      console.error(`${UPDATE_ERROR_MSG}: ${error}`);
      showToast({
        message: UPDATE_ERROR_MSG,
        type: AlertType.ERROR,
      });
    }
  }, [
    existingTemplateId,
    coachingTemplateName,
    coachingTemplatePrompt,
    updateCoachingTemplate,
    handleApiResponse,
    navigateToCoachingRubrics,
    showToast,
  ]);

  const destructiveAction = isExistingTemplateUsed ? 'Archive' : 'Delete';
  const destructiveActionOnClick = isExistingTemplateUsed
    ? handleArchiveCoachingTemplate
    : handleDeleteCoachingTemplate;

  const destructiveButtonConfirmText = (
    <>
      Are you sure you want to {destructiveAction.toLowerCase()}&nbsp;
      <span className="font-medium">{coachingTemplateName}</span>? This action cannot be undone.
    </>
  );

  return (
    <>
      <Footer>
        <div className="flex w-full justify-end">
          {/* Edit existing template actions */}
          {existingTemplateId && (
            <ButtonGroup>
              <TextButton
                text="Save"
                loading={isUpdatingCoaching}
                onClick={handleEditCoachingTemplate}
                disabled={!coachingTemplateName || !coachingTemplatePrompt}
              />
              <TextButton
                text={destructiveAction}
                onClick={() => setIsConfirmModalOpen(true)}
                color={ButtonColor.DESTRUCTIVE}
                loading={isDeletingCoaching || isArchivingCoaching}
                disabled={isExistingTemplateDefault}
                tooltip={
                  isExistingTemplateDefault
                    ? `Default coaching template cannot be ${destructiveAction.toLowerCase()}d`
                    : undefined
                }
              />
            </ButtonGroup>
          )}
          {/* Create new template actions */}
          {!existingTemplateId && (
            <ButtonGroup>
              <TextButton
                text="Save"
                loading={isCreatingCoaching}
                onClick={handleCreateCoachingTemplate}
                disabled={!coachingTemplateName || !coachingTemplatePrompt}
              />
              <TextButton text="Delete" onClick={handleDeleteCoachingTemplate} color={ButtonColor.DESTRUCTIVE} />
            </ButtonGroup>
          )}
        </div>
      </Footer>
      <ConfirmModal
        isOpen={isConfirmModalOpen}
        buttonText={destructiveAction}
        setIsOpen={setIsConfirmModalOpen}
        onConfirm={destructiveActionOnClick}
        isLoading={isDeletingCoaching || isArchivingCoaching}
        title={`${destructiveAction} coaching template`}
        confirmText={destructiveButtonConfirmText}
        destructive
      />
    </>
  );
};

export default ManageCoachingTemplateFooter;
